<!--
 * @Description: 路区代码下拉全局
-->
<template>
  <CosInputView v-if="view" :mode="mode" :value="viewTxt" />
  <a-select
    v-bind="$attrs"
    v-else
    v-model:value="data"
    :mode="mode"
    style="width: 100%"
    ref="roadAreaSelect"
    @change="change"
    allowClear
    @popupScroll="popupScroll"
    :showSearch="true"
    :filter-option="false"
    @search="fetchRoadArea"
    :placeholder="placeholder || t('common.pleaseSelect')"
  >
    <a-select-option v-for="item in optionPageComputed" :key="item.roadAreaCode" :value="item.roadAreaCode">{{ item.roadAreaCode}}</a-select-option>
  </a-select>
</template>
<script lang="ts" setup>
import { ref, onMounted, watch, computed } from 'vue'
import { debounce, differenceBy, uniqBy } from 'lodash-es'
import CosInputView from '@/components/Antd/CosView/input.vue'
import { useI18n } from 'vue-i18n'
import { getLbuRoadAreaSelectList } from '@/api/yuntuSales/roadAreaCode'
const { t } = useI18n()

const props = defineProps({
  value: {
    type: [String, Number, Array]
  },
  initParams: {
    type: Object,
    default: () => {
      return {}
    }
  },
  placeholder: {
    type: String
  },
  view: {
    type: Boolean
  },
  mode: {
    type: String
  },
  defaultValue: {
    type: String
  },
  // 是否根据登录员工的数据权限查询，默认是false
  permission: {
    type: Boolean,
    default: false
  },
  // 单选时返回的字段：'roadAreaCode' 或 'roadAreaUserList'（默认）
  update: {
    type: String,
    default: 'roadAreaUserList'
  }
})
const data = ref()
const roadAreaSelect = ref()
const emit = defineEmits(['update:value', 'change', 'onSelect'])
const optionPage = ref<any[]>([])
const pageNumber = ref(1)
const searchTxt = ref('')
const valueList = ref<any[]>([])
const change = (value: any) => {
  console.log(value)
  if (props.mode === 'multiple') {
    // 多选模式：返回 roadAreaCode 列表
    emit('update:value', value)
    emit('change', value)
    const records = optionPageComputed.value.filter((item: any) => value.includes(item.roadAreaCode))
    emit('onSelect', records)
  } else {
    // 单选模式：根据 update 属性返回 roadAreaCode 或 roadAreaUserList
    const record = optionPage.value.find((v: any) => v.roadAreaCode === value)
    if (props.update === 'roadAreaCode') {
      emit('update:value', value)
    } else {
      emit('update:value', record?.roadAreaUserList)
    }
    emit('change', value)
    emit('onSelect', record)
  }
}
const popupScroll = (e: any) => {
  const { scrollHeight, scrollTop, clientHeight } = e.target
  if (scrollHeight - scrollTop === clientHeight) {
    pageNumber.value = pageNumber.value + 1
    fetch()
  }
}
const optionPageComputed = computed(() => {
  if (searchTxt.value) {
    return optionPage.value
  }
  return uniqBy([...valueList.value, ...optionPage.value], 'roadAreaCode')
})
// 是否请求接口获取值对应的详细信息
const needFetchValue = (v: any[]) => {
  if (v && v.length) {
    const vL = v.map((code) => {
      return {
        roadAreaCode: code
      }
    })
    const valueArr = differenceBy(vL, optionPage.value, 'roadAreaCode')
    if (valueArr && valueArr.length) {
      // 这里可以根据实际情况调用接口获取详细信息
      // 如果没有对应接口，valueList 保持为空即可
      valueList.value = valueArr
    }
  }
}
// 显示文本
const viewTxt = computed(() => {
  console.log('One:', props.value)
  if (optionPageComputed.value && props.value) {
    if (props.mode === 'multiple') {
      // 多选模式：props.value 是 roadAreaCode 数组
      if (Array.isArray(props.value)) {
        const result: any = []
        props.value.forEach((val) => {
          const find = optionPageComputed.value.find((item) => item.roadAreaCode === val)
          if (find) {
            result.push(find.roadAreaCode)
          } else {
            result.push(val)
          }
        })
        return result.join(';') || '--'
      }
      return props.value || '--'
    } else {
      // 单选模式：根据 update 属性判断 props.value 的类型
      if (props.update === 'roadAreaCode') {
        // props.value 是 roadAreaCode
        const find = optionPageComputed.value.find((item) => item.roadAreaCode === props.value)
        if (find) {
          return find.roadAreaCode
        }
        return props.value || '--'
      } else {
        // props.value 是 roadAreaUserList（数组）
        if (Array.isArray(props.value)) {
          // 通过 roadAreaUserList 查找对应的 roadAreaCode
          const find = optionPageComputed.value.find((item) =>
            JSON.stringify(item.roadAreaUserList) === JSON.stringify(props.value)
          )
          if (find) {
            return find.roadAreaCode
          }
        }
        return props.value || '--'
      }
    }
  }
  return props.value || '--'
})
const focus = () => {
  roadAreaSelect.value.focus()
}
defineExpose({
  focus
})
onMounted(() => {
  fetch()
})
const fetchRoadArea = debounce((txt: string) => {
  // 去掉前后空格
  searchTxt.value = txt.trim()
  fetch()
}, 500)
const fetch = () => {
  getLbuRoadAreaSelectList({
    roadAreaCode: searchTxt.value
  }).then((res) => {
    optionPage.value = res.data || []
  })
}
watch(
  () => props.value,
  (v) => {
    if (!v) {
      data.value = v
      // 处理搜索
      if (searchTxt.value) {
        fetchRoadArea('')
      }
    } else if (Array.isArray(v)) {
      if (props.mode === 'multiple') {
        // 多选模式：v 是 roadAreaCode 数组
        if (JSON.stringify(v) !== JSON.stringify(data.value)) {
          data.value = v
          needFetchValue(v)
        }
      } else {
        // 单选模式：根据 update 属性判断 v 的类型
        if (props.update === 'roadAreaCode') {
          // v 是 roadAreaCode
          if (v !== data.value) {
            data.value = v
          }
        } else {
          // v 是 roadAreaUserList，需要找到对应的 roadAreaCode 来设置 data.value
          const find = optionPageComputed.value.find((item) =>
            JSON.stringify(item.roadAreaUserList) === JSON.stringify(v)
          )
          if (find && find.roadAreaCode !== data.value) {
            data.value = find.roadAreaCode
          }
        }
      }
    } else if (typeof v === 'string' && v !== data.value) {
      data.value = v
      needFetchValue([v])
    }
  },
  { immediate: true }
)
</script>
